import { createContext, useContext, type ReactNode } from 'react'

import type { StagedAction } from './staged-actions/types'

export { useStagedActionStatus, type StagedActionStatusInfo } from './staged-actions/status-hooks'

/**
 * The stable subset of the staged-action mutation hooks that action cells
 * actually invoke. Provided once at the overlay root by `StagedActionsProvider`
 * so cells don't each mount the underlying hooks.
 */
export interface StagedActionCallbacks {
  invokeStagedAction: (id: string) => Promise<void>
  undoStagedAction: (id: string) => Promise<string>
  bulkArchiveStagedActions: (ids: Array<string>) => Promise<void>
  /** Single-row archive that lingers for the undo window; `undoArchiveStagedAction` takes it back. */
  archiveStagedActionWithUndo: (id: string) => Promise<void>
  undoArchiveStagedAction: (id: string) => Promise<void>
  markActionExpired: (id: string) => void
  markActionAsRead: (id: string, currentAction?: StagedAction) => void
}

const StagedActionCallbacksContext = createContext<StagedActionCallbacks | null>(null)

export function StagedActionCallbacksProvider({
  value,
  children,
}: {
  value: StagedActionCallbacks
  children: ReactNode
}) {
  return <StagedActionCallbacksContext.Provider value={value}>{children}</StagedActionCallbacksContext.Provider>
}

export function useStagedActionCallbacks(): StagedActionCallbacks {
  const ctx = useContext(StagedActionCallbacksContext)
  if (!ctx) {
    throw new Error('useStagedActionCallbacks must be used within a StagedActionCallbacksProvider')
  }
  return ctx
}
